import React, { useState, SetStateAction, useEffect, ReactNode, useMemo } from 'react';
import {
  Typography,
  Button,
  Grid,
  Box,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDateTimePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { isValid, isBefore } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { LeafletMouseEvent } from 'leaflet';
import MapMarker from '../MapMarker';
import api from '../../services/api';
import { googleApi } from '../../services/api';
import { useStyles } from './styles';
import AutoCompleteField from '../AutoCompleteVirtualized';
import ShowSnackbar, { SetSnackbarType } from '../SnackBar';
import { GoogleMapsReverseResponseType } from '../../@types/geocodingTypes';
import { viaNames } from './viaNames';
import SubInputWarn from '../SubInputWarn';
import { BoundaryPolygonResponseType } from '../../@types/boundarypolygonTypes';
import { update } from 'lodash';
import { dateToBRT } from '../../utils/formatDate';

const initialAddress: GoogleMapsReverseResponseType = {
  status: '',
  plus_code: {
    compound_code: '',
    global_code: '',
  },
  results: [
    {
      address_components: [
        {
          long_name: '',
          short_name: '',
          types: [' '],
        },
      ],
      formatted_address: '',
      geometry: {
        location: {
          lat: 0,
          lng: 0,
        },
        location_type: '',
      },
    },
  ],
};

interface Coordinates {
  lat: number;
  lng: number;
}

interface UpdateLocation {
  address1: string;
  address2?: string;
  refPoint?: string;
  neighborhood?: string;
}

interface IProps {
  setLocation: React.Dispatch<SetStateAction<GoogleMapsReverseResponseType>>;
  latlng: Coordinates;
  setLatLng: React.Dispatch<SetStateAction<Coordinates>>;
  period: string;
  setPeriod: React.Dispatch<SetStateAction<string>>;
  requiredFields: { key: string; message: string }[];
  updateLocation?: UpdateLocation;
  adresses: { address1: string; address2: string; neighborhood: string };
  setAdresses: React.Dispatch<
    React.SetStateAction<{ address1: string; address2: string; neighborhood: string }>
  >;
  reference: string;
  setReference: React.Dispatch<React.SetStateAction<string>>;
  originalAddress?: string;
  locationType?: string;
}

type EnderecoFormatado = {
  endereco: string;
  numero: string;
  bairro: string;
};

const isInList = (option: string, list: { name: string }[]): string => {
  const index = list.find((item) => item.name === option);
  return index ? option : 'NÃO INFORMADO';
};

const restData: {}[] = [];

const LocalizationSection: React.FC<IProps> = ({
  setLocation,
  latlng,
  setLatLng,
  period,
  setPeriod,
  requiredFields,
  updateLocation,
  adresses,
  reference,
  setReference,
  setAdresses,
  originalAddress,
  locationType,
}) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [address1, setAddress1] = useState<string>(adresses.address1);
  const [address2, setAddress2] = useState<string>(adresses.address2);
  const [neighborhoodNames, setNeighborhoodsNames] = useState<{ name: string }[]>([]);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<string>(
    adresses.neighborhood || 'NÃO INFORMADO',
  );
  const [geoLocation, setGeolocation] = useState<Coordinates>({ lat: 0, lng: 0 });
  const [snackBar, setSnackbar] = useState<SetSnackbarType>({
    open: false,
    message: '',
  });
  const [toUpdate, setToUpdate] = useState(false);

  const [referenceText, setReferenceText] = useState<string>(reference);
  const authHeader = React.useMemo(() => {
    return { Authorization: `Token ${localStorage.getItem('auth')}` };
  }, []);

  const geocodeKey = `${process.env.REACT_APP_GMAPS_API_KEY}`;
  const neighbordhoodBoundaryId = `${process.env.REACT_APP_NEIGHBORHOOD_BOUNDARY_ID}`;

  const classes = useStyles();
  function encontrarBairroMaisSimilar(nomeDigitado: string): string | null {
    if (neighborhoodNames.length === 0) {
      return null;
    }

    let bairroMaisSimilar: string | null = null;
    let menorDistancia: number = Number.MAX_VALUE;
    for (const bairro of neighborhoodNames) {
      const distancia = calcularDistanciaLevenshtein(nomeDigitado, bairro.name);

      if (distancia < menorDistancia) {
        menorDistancia = distancia;
        bairroMaisSimilar = bairro.name;
      }
    }

    return bairroMaisSimilar;
  }

  function calcularDistanciaLevenshtein(s1: string, s2: string): number {
    const m = s1.length;
    const n = s2.length;
    const dp: number[][] = [];

    for (let i = 0; i <= m; i++) {
      dp[i] = [i];
    }

    for (let j = 0; j <= n; j++) {
      dp[0][j] = j;
    }

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
      }
    }

    return dp[m][n];
  }

  async function getNeighborhoodsName() {
    const count = await api.get('/api/boundarypolygons/', {
      headers: authHeader,
      params: {
        archived: false,
        details_only: true,
        limit: 1,
        boundary: neighbordhoodBoundaryId,
      },
    });

    const { data } = await api.get<BoundaryPolygonResponseType>(
      `/api/boundarypolygons/?nogeom=true`,
      {
        headers: authHeader,
        params: {
          archived: false,
          details_only: true,
          limit: count.data.count,
          boundary: neighbordhoodBoundaryId,
        },
      },
    );
    data.results = data.results.sort((a, b) => {
      return a.data.NOME.localeCompare(b.data.NOME);
    });

    setNeighborhoodsNames((prev) => {
      let nameList = [...prev];
      nameList = data.results.map((el) => {
        return { name: el.data.NOME };
      });

      nameList.unshift({ name: 'NÃO INFORMADO' });
      return nameList;
    });
  }

  // Pesquisar a partir do ponto adicionado no mapa
  const getAddress = async (event: LeafletMouseEvent) => {
    setGeolocation({ lat: event.latlng.lat, lng: event.latlng.lng });

    try {
      const { data } = await googleApi.get<GoogleMapsReverseResponseType>(
        `/api/geocode/json?latlng=${event.latlng.lat},${event.latlng.lng}&key=${geocodeKey}`,
      );

      setSelectedNeighborhood(data.results[0].address_components[2].long_name);
      setReference(data.results[0].address_components[0].long_name);
      setLocation(data);
      setAddress1(
        `${
          data.results[0].address_components[1].long_name ||
          data.results[0].address_components[0].long_name
        }`,
      );
      setAdresses((prev) => {
        return {
          ...prev,
          address1: `${
            data.results[0].address_components[1].long_name ||
            data.results[0].address_components[0].long_name
          }`,
          neighborhood: `${data.results[0].address_components[2].long_name}`,
        };
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Não foi possível realizar a Geolocalização. Tente novamente.',
      });
    }
  };

  const handleCoordinate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    const value = Number(event.target.value);

    // Atualiza as coordenadas de latitude ou longitude
    const newGeoLocation =
      name === 'Latitude'
        ? { ...geoLocation, lat: value }
        : { ...geoLocation, lng: value };

    setGeolocation(newGeoLocation); // Atualiza o estado das coordenadas

    // Verifica se não estão nulas
    if (newGeoLocation.lat && newGeoLocation.lng) {
      await getAddressFromCoordinates(newGeoLocation);
    }
  };

  const handleAddressClick = () => {
    // Não faz nada quando o usuário clica no campo de endereço
    // Ou você pode limpar o campo de address1 se desejar
    setAddress1('');
  };

  const getAddressFromCoordinates = async (coordinates: Coordinates) => {
    try {
      const { data } = await googleApi.get<GoogleMapsReverseResponseType>(
        `/api/geocode/json?latlng=${coordinates.lat},${coordinates.lng}&key=${geocodeKey}`,
      );

      // Preenche o endereço 1 com o primeiro componente de endereço encontrado
      const formattedAddress =
        data.results[0]?.address_components[1]?.long_name ||
        data.results[0]?.address_components[0]?.long_name ||
        'Endereço não encontrado';

      setAddress1(formattedAddress); // Atualiza o estado do endereço1

      // Atualiza outros campos, como bairro e referência, conforme necessário
      setSelectedNeighborhood(data.results[0].address_components[2].long_name);
      setReference(data.results[0].address_components[0].long_name);
      setLocation(data); // Atualiza a localização completa
      setAdresses((prev) => ({
        ...prev,
        address1: formattedAddress,
      }));
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Não foi possível obter o endereço. Tente novamente.',
      });
    }
  };

  useEffect(() => {
    setLatLng(geoLocation);
  }, [geoLocation]);

  function extrairEnderecoCompleto(data: any): EnderecoFormatado {
    const components = data?.results?.[0]?.address_components || [];

    const getComponent = (type: string) =>
      components.find((c: any) => c.types.includes(type))?.long_name || '';

    return {
      endereco: getComponent('route'),
      numero: getComponent('street_number'),
      bairro: getComponent('sublocality') || getComponent('sublocality_level_1'),
    };
  }

  // Serve para pesquisar a localização
  const getGeolocation = async () => {
    setLocation(initialAddress);
    let treatString = searchValue.replace(/[\u0020-\u002F]/g, ' ');
    treatString = treatString.replace(/\s/g, ' ');
    if (treatString !== '') {
      try {
        const { data: gmaps } = await googleApi.get(
          `/api/geocode/json?address=${treatString}&language=pt-BR&key=${geocodeKey}`,
        );

        const enderecoUm = extrairEnderecoCompleto(gmaps);

        setGeolocation({
          lat: Number(gmaps.results[0].geometry.location.lat),
          lng: Number(gmaps.results[0].geometry.location.lng),
        });

        setLatLng({
          lat: Number(gmaps.results[0].geometry.location.lat),
          lng: Number(gmaps.results[0].geometry.location.lng),
        });

        const { data } = await googleApi.get<GoogleMapsReverseResponseType>(
          `/api/geocode/json?latlng=${gmaps.results[0].geometry.location.lat},${gmaps.results[0].geometry.location.lng}&key=${geocodeKey}`,
        );

        setSelectedNeighborhood(data.results[0].address_components[2].long_name);
        setReference(data.results[0].address_components[0].long_name);
        setLocation(data);
        setAddress1(
          `${
            // data.results[0].address_components[1].long_name ||
            // data.results[0].address_components[0].long_name
            enderecoUm.endereco
          }`,
        );
        setAdresses((prev) => {
          return {
            ...prev,
            // address1: `${data.results[0].address_components[0].long_name}`,
            address1: `${enderecoUm.endereco || address1}`,
            // neighborhood: `${data.results[0].address_components[1].long_name}`,
            neighborhood: `${enderecoUm.bairro}`,
          };
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Não foi possível encontrar o endereço informado.',
        });
      }
    } else {
      setSnackbar({
        open: true,
        message: 'Informe um endereço para pesquisar..',
      });
    }
  };

  function handlePeriodChange(value: Date | null) {
    if (value && isValid(value) && isBefore(value, new Date())) {
      setPeriod(dateToBRT(value));
    }
  }

  useEffect(() => {
    updateLocation?.address1 && address1 === '' ? setToUpdate(true) : setToUpdate(false);
    if (updateLocation && updateLocation.address1 && toUpdate) {
      setAddress1(updateLocation.address1);
    }
    if (updateLocation && updateLocation.address2 && toUpdate) {
      setAddress2(updateLocation.address2);
    }
    setSelectedNeighborhood(adresses.neighborhood);
  }, [updateLocation, adresses.neighborhood]);

  useEffect(() => {
    getNeighborhoodsName();
  }, [authHeader]);

  return (
    <Grid item sm={12} xs={12}>
      <Box className={classes.sectionContainer}>
        <Typography variant="h6">Localização e hora do incidente</Typography>
        <Typography variant="subtitle1">Localização</Typography>
        <Grid container spacing={2} md={12} xs={12} className={classes.searchBar}>
          <Grid item sm={12} md={10} xs={12}>
            <InputLabel className={classes.label} htmlFor="search">
              Pesquisar endereço
            </InputLabel>
            <input
              name="search"
              type="text"
              placeholder="Rua, nº (opcional), bairro, cidade, estado"
              onChange={(event) => setSearchValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault(); // Impede o envio do formulário
                  getGeolocation(); // Chama a função de pesquisa
                }
              }}
              value={searchValue}
              className={classes.input}
            />
          </Grid>
          <Grid item sm={2} xs={2}>
            <Button
              className={classes.buttonSearch}
              variant="contained"
              color="primary"
              onClick={getGeolocation}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault(); // Impede o envio do formulário
                  getGeolocation(); // Chama a função de pesquisa
                }
              }}
            >
              Pesquisar
            </Button>
          </Grid>
        </Grid>
        <Grid container md={12} xs={12} className={classes.mapContainer}>
          <MapMarker onClick={getAddress} point={[latlng.lat, latlng.lng]} />
        </Grid>
        {originalAddress ? (
          <Grid container spacing={3} md={12} xs={12}>
            <Grid item sm={12} md={12} xs={12}>
              <InputLabel className={classes.label} htmlFor="search">
                Endereço Original
              </InputLabel>
              <input
                autoComplete="off"
                name="data.driverIncidenteDetails"
                type="text"
                disabled={true}
                value={originalAddress}
                className={classes.input}
              />
            </Grid>
          </Grid>
        ) : (
          <></>
        )}
        {locationType ? (
          <Grid container spacing={3} md={12} xs={12}>
            <Grid item sm={12} md={12} xs={12}>
              <InputLabel className={classes.label} htmlFor="search">
                Tipo de Localização
              </InputLabel>
              <input
                autoComplete="off"
                name="data.driverIncidenteDetails"
                type="text"
                disabled={true}
                value={locationType}
                className={classes.input}
              />
            </Grid>
          </Grid>
        ) : (
          <></>
        )}

        <Grid container spacing={3} md={12} xs={12}>
          <Grid item sm={12} md={6} xs={12}>
            <InputLabel className={classes.label} htmlFor="search">
              Endereço 1
            </InputLabel>
            <input
              autoComplete="off"
              name="data.driverIncidenteDetails"
              type="text"
              placeholder="Informe o endereço 1"
              // onChange={(event) => {
              //   setAddress1(event.target.value);
              // }}
              onChange={(event) => {
                const { value } = event.target;
                setAdresses((prev) => ({
                  ...prev,
                  address1: value,
                }));
              }}
              value={adresses.address1}
              // value={adresses.address1}
              className={`${classes.input} ${
                requiredFields.filter((item) => item.key === 'Endereço 1').length
                  ? classes.errorIput
                  : ''
              }`}
            />
            <SubInputWarn
              type="error"
              formWarns={requiredFields.filter((item) => item.key === 'Endereço 1')}
            />
          </Grid>
          <Grid item sm={12} md={6} xs={12}>
            <InputLabel className={classes.label} htmlFor="search">
              Endereço 2
            </InputLabel>
            <AutoCompleteField
              options={viaNames}
              placeholder="Informe o endereço 2"
              value={address2}
              onChange={(val) => setAddress2(val)}
              onBlur={() =>
                setAdresses((prev) => {
                  return { ...prev, address2 };
                })
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={3} md={12} xs={12}>
          <Grid item sm={12} md={6} xs={12}>
            <InputLabel className={classes.label} htmlFor="search">
              Número ou ponto de referência
            </InputLabel>
            <input
              name="Endereço 2"
              type="text"
              placeholder="Número ou ponto de referência"
              onChange={(event) => setReference(event.target.value)}
              value={reference}
              className={classes.input}
              onBlur={() => setReference(reference)}
            />
          </Grid>
          <Grid item sm={12} md={6} xs={12}>
            <InputLabel className={classes.label} htmlFor="search">
              Bairro
            </InputLabel>
            <AutoCompleteField
              options={neighborhoodNames}
              placeholder="Informe o bairro"
              value={selectedNeighborhood}
              onChange={(val) => setSelectedNeighborhood(val as string)}
              onBlur={() =>
                setAdresses((prev) => {
                  return { ...prev, neighborhood: selectedNeighborhood };
                })
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={3} md={12} xs={12}>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <InputLabel className={classes.label} htmlFor="Latitude">
              Latitude
            </InputLabel>
            <input
              name="Latitude"
              type="number"
              placeholder="Latitude"
              onChange={(e) => {
                const value = e.target.value;

                // Permite apagar completamente o valor (string vazia)
                if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                  handleCoordinate(e);
                }
              }}
              value={geoLocation.lat === 0 ? latlng.lat || '' : geoLocation.lat}
              className={classes.input}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <InputLabel className={classes.label} htmlFor="Longitude">
              Longitude
            </InputLabel>
            <input
              name="Longitude"
              type="number" // Alterado para text
              placeholder="Longitude"
              onChange={(e) => {
                const value = e.target.value;

                // Permite apagar completamente o valor (string vazia)
                if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                  handleCoordinate(e);
                }
              }}
              value={geoLocation.lng === 0 ? latlng.lng || '' : geoLocation.lng}
              className={classes.input}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6} lg={4}>
            <InputLabel className={classes.label} htmlFor="Longitude">
              Data da ocorrência
            </InputLabel>
            <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
              <KeyboardDateTimePicker
                variant="inline"
                ampm={false}
                maxDate={Date.now()}
                format="dd/MM/yyyy HH:mm"
                className={classes.textField}
                value={period || null}
                onChange={(event) => handlePeriodChange(event)}
                placeholder="dd/mm/aaaa hh:mm"
                inputVariant="outlined"
                inputProps={{ style: { padding: '0 0 0 15px', height: 40 } }}
                error={
                  !!requiredFields.filter((item) => item.key === 'incidentDate').length
                }
                helperText=""
              />
            </MuiPickersUtilsProvider>
            <SubInputWarn
              type="error"
              formWarns={requiredFields.filter((item) => item.key === 'incidentDate')}
            />
          </Grid>
        </Grid>
      </Box>
      <ShowSnackbar
        open={snackBar.open}
        message={snackBar.message}
        setSnackbar={setSnackbar}
      />
    </Grid>
  );
};

export default LocalizationSection;
